import _ from 'lodash';
import {getStepNameSelector} from 'stores/selectors/wizardSelectors';
import {getSetupWizardStepsList} from 'contents/SetupWizard/content/SetupWizardContentBusiness';

import {parseMessage} from 'lib/stringHelper';
import FormattedHTMLMessage from '../../../components/FormattedHTMLMessage';
import {ViewMode} from '../../../constants/dataEnum';
import {
  DefaultInstrumentV1StepsData,
  DefaultInstrumentV2StepsData,
  SetupWizardStep,
  WizardFlag,
} from '../../../constants/SetupWizardEnum';
import {setCurrentStepData, setStepData, setWizardAllowedNext} from '../../actions/wizardStateAction';
import {isIntlTextValueExists, resolveIntl} from '../commonBusiness';
// eslint-disable-next-line import/no-cycle
import {isV2Instrument} from '../instrumentStatusBusiness';

const intl = resolveIntl();

export const isOnSetupWizardView = () => (dispatch, getState) => getState().appState.viewMode === ViewMode.SETUP_WIZARD;

export const getSetupWizardCurrentStepNumber = () => (dispatch, getState) => {
  const {wizardState, appState} = getState();

  const {viewMode} = appState;

  return viewMode === ViewMode.SETUP_WIZARD ? wizardState.currentWizardView : null;
};

export const getDefaultStepsData = (stepName) =>
  isV2Instrument() ? DefaultInstrumentV2StepsData[stepName] : DefaultInstrumentV1StepsData[stepName];

export const initializeDefaultStepData = (step, resetSubstep) => (dispatch, getState) => {
  const stepNumber = step || dispatch(getSetupWizardCurrentStepNumber());
  const stepData = dispatch(getSetupWizardStepData(stepNumber));
  const stepName = _.isNil(step) ? getStepNameSelector(getState()) : getSetupWizardStepsList()[step];

  const {currentSubstep} = stepData || {};

  const requiredData = {
    isSkippable: stepName === SetupWizardStep.REPORT ? false : (stepData?.isSkippable ?? true),
    isStepProcessed: stepName === SetupWizardStep.REPORT ? true : (stepData?.isStepProcessed ?? false),
    step: stepNumber,
    stepName,
  };

  const defaultData = getDefaultStepsData(stepName);

  let newData = {...defaultData} || {};

  if (!defaultData) {
    newData = {...newData, totalSubstep: 1};
  }

  if (resetSubstep || !currentSubstep) {
    newData = {...newData, currentSubstep: 1, processedSubstep: 0};
  }

  newData = {...newData, ...requiredData};
  dispatch(setStepData(stepNumber, newData));
  return newData;
};

export const getSetupWizardCurrentStepData = () => (dispatch, getState) => {
  const {wizardState} = getState();
  const {currentWizardView, wizardViewData} = wizardState;
  let stepData = wizardViewData[currentWizardView];

  if (!stepData) stepData = dispatch(initializeDefaultStepData(currentWizardView));

  return stepData;
};

export const getSetupWizardStepData = (step) => (dispatch, getState) => {
  const {wizardState} = getState();
  const {wizardViewData} = wizardState;

  return wizardViewData[step];
};

export const getSetupWizardFlagMessage = (flag, returnAsString = true, isDefaultMessage = false) =>
  // eslint-disable-next-line func-names
  function (dispatch) {
    const stepData = dispatch(getSetupWizardCurrentStepData());
    const {stepName, currentSubstep, totalSubstep} = stepData || {};

    if (flag === WizardFlag.NONE) return '';

    let intlId =
      isDefaultMessage && flag !== WizardFlag.SUCCESS
        ? `setupWizard.wizardIndicator.${flag}.default`
        : `setupWizard.wizardIndicator.${flag}.step.${stepName}`;

    if (totalSubstep > 1 && !isDefaultMessage) {
      intlId = `${intlId}.substep.${currentSubstep}`;
    }

    const isMsgValid = isIntlTextValueExists(intlId);

    if (!isMsgValid) {
      if (!isDefaultMessage) return dispatch(getSetupWizardFlagMessage(flag, returnAsString, true));
      // eslint-disable-next-line react/jsx-no-useless-fragment
      return returnAsString ? '' : <></>;
    }

    return returnAsString ? intl.formatMessage({id: intlId}) : <FormattedHTMLMessage id={intlId} />;
  };

export const incrementSetupWizardSubstep = () => (dispatch) => {
  const currentStepData = dispatch(getSetupWizardCurrentStepData());
  const {currentSubstep, stepName} = currentStepData || {};

  if (!currentSubstep) return;

  const {totalSubstep} = getDefaultStepsData(stepName);

  if (currentSubstep >= totalSubstep) return;

  dispatch(setCurrentStepData({currentSubstep: currentSubstep + 1}));
};

export const decrementSetupWizardSubstep = () => (dispatch) => {
  const {currentSubstep} = dispatch(getSetupWizardCurrentStepData());

  if (!currentSubstep) return;
  if (currentSubstep === 1) return;

  dispatch(setCurrentStepData({currentSubstep: currentSubstep - 1}));
};

export const getSetupWizardNextStepData = () => (dispatch, getState) => {
  const {wizardState} = getState();
  const {currentWizardView, wizardViewData} = wizardState;

  const nextStepNumber = currentWizardView + 1;
  let nextStep = wizardViewData[nextStepNumber];

  if (!nextStep) nextStep = dispatch(initializeDefaultStepData(nextStepNumber));

  return nextStep;
};

export const getSetupWizardPreviousStepData = () => (dispatch, getState) => {
  const {wizardState} = getState();
  const {currentWizardView, stepName, wizardViewData} = wizardState;

  if (stepName === SetupWizardStep.WELCOME_SCREEN) return {};

  return wizardViewData[currentWizardView - 1];
};

export const isOnFirstSubstep = () => (dispatch, getState) => {
  const currentStepData = dispatch(getSetupWizardCurrentStepData());
  const stepName = getStepNameSelector(getState());

  const defaultCurrentStepData = getDefaultStepsData(stepName);

  if (!defaultCurrentStepData) return true;

  const {currentSubstep} = currentStepData;

  if (currentSubstep === 0) return true;

  return false;
};

export const isOnLastSubstep = () => (dispatch, getState) => {
  const currentStepData = dispatch(getSetupWizardCurrentStepData());
  const stepName = getStepNameSelector(getState());

  const defaultCurrentStepData = getDefaultStepsData(stepName);

  if (!defaultCurrentStepData) return true;

  const {totalSubstep} = defaultCurrentStepData;
  const {currentSubstep} = currentStepData;

  if (currentSubstep === totalSubstep) return true;

  return false;
};

export const getSetupWizardBodyFormat = () => (dispatch) => {
  const stepData = dispatch(getSetupWizardCurrentStepData());

  const {currentSubstep, stepName, totalSubstep} = stepData || {};
  const stepNameInKebabCase = _.kebabCase(stepName);

  let intlTextFormat = stepName;
  let classNameFormat = stepNameInKebabCase;
  let classNameContainerFormat = stepNameInKebabCase;

  if (totalSubstep > 1) {
    classNameFormat = `${classNameFormat}-substep-${currentSubstep}`;
    intlTextFormat = `${intlTextFormat}.substep${currentSubstep}`;

    classNameContainerFormat = classNameFormat;

    if (stepName === SetupWizardStep.VISION_CALIBRATION) {
      const {scanSuccess, hasScanned} = stepData;

      classNameFormat = `${classNameFormat}-scan-${hasScanned}`;
      intlTextFormat = `${intlTextFormat}.hasScanned.${hasScanned}.scanSuccess.${scanSuccess}`;
    }
  }

  return {classNameFormat, intlTextFormat, classNameContainerFormat, currentSubstep, stepName};
};

export const getSetupWizardStepperLabel = () => {
  const stepList = getSetupWizardStepsList();

  const excludedSteps = [SetupWizardStep.WELCOME_SCREEN, SetupWizardStep.REPORT, SetupWizardStep.FINISH_SCREEN];

  return stepList
    .filter((step) => !excludedSteps.includes(step))
    .map((step) => parseMessage(`setupWizard.stepper.label.${step}`));
};

export const setCurrentStepAsProcessed = () => (dispatch) => {
  dispatch(setCurrentStepData({isStepProcessed: true}));
  dispatch(setWizardAllowedNext(true));
};

const getStepNumberByName = (stepName) => {
  const stepsList = getSetupWizardStepsList();
  const stepNumber = stepsList.findIndex((step) => step === stepName);

  return stepNumber;
};

export const setStepDataByName = (stepName, data) => (dispatch) => {
  const stepNumber = getStepNumberByName(stepName);
  if (stepNumber === -1) return;

  dispatch(setStepData(stepNumber, data));
};

export const CommonSetupWizardBusiness = {
  getStepNumberByName,
};
